import { SerializableInitError, SerializableInitStep } from '@extension/wa-communication-layer/core/init-error-tracker';
import { sendInitErrorReport, InitErrorReportData } from '@extension/wa-communication-layer/api/api-calls';
import { AlertTriangle, CheckCircle, Clock, XCircle, Copy, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

// Event names must match the ones in init-error-tracker.ts
const INIT_ERROR_EVENT = 'bt-init-error-update';
const INIT_ERROR_REQUEST_EVENT = 'bt-init-error-request'; // Request current state from tracker
const INIT_ERROR_SEND_REPORT_EVENT = 'bt-init-error-send-report'; // Receive report data from MAIN world

interface InitErrorEventData {
  errors: SerializableInitError[];
  steps: SerializableInitStep[];
  isComplete: boolean;
  hasErrors: boolean;
}

interface InitErrorIndicatorProps {
  className?: string;
}

function InitErrorIndicator({ className }: InitErrorIndicatorProps) {
  const [errors, setErrors] = useState<SerializableInitError[]>([]);
  const [steps, setSteps] = useState<SerializableInitStep[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  // Only show error indicator if bt-debug is enabled
  const isDebugEnabled = localStorage.getItem('bt-debug') === 'true';

  console.log('[INIT UI] InitErrorIndicator component rendered', {
    errorsCount: errors.length,
    stepsCount: steps.length,
    isComplete,
    isDebugEnabled
  });

  useEffect(() => {
    console.log('[INIT UI] InitErrorIndicator useEffect running - setting up document event listener');

    const handleInitErrorEvent = (event: Event) => {
      const customEvent = event as CustomEvent<InitErrorEventData>;
      const data = customEvent.detail;

      console.log('[INIT UI] 📥 Received event from document:', {
        errorsCount: data.errors.length,
        stepsCount: data.steps.length,
        isComplete: data.isComplete,
        hasErrors: data.hasErrors
      });

      setErrors(data.errors);
      setSteps(data.steps);
      setIsComplete(data.isComplete);
    };

    // Handle send-report event from MAIN world - make API call from content script context
    const handleSendReportEvent = async (event: Event) => {
      const customEvent = event as CustomEvent<InitErrorReportData>;
      const reportData = customEvent.detail;

      console.log('[INIT UI] 📧 Received send-report event, making API call...', {
        errorsCount: reportData.errors.length
      });

      try {
        const result = await sendInitErrorReport(reportData);
        if (result.success !== false) {
          console.log('[INIT UI] ✅ Error report sent to backend successfully');
        } else {
          console.log('[INIT UI] ❌ Failed to send error report:', result.error);
        }
      } catch (error) {
        console.error('[INIT UI] ❌ Error sending report to backend:', error);
      }
    };

    // Listen for events from the MAIN world (wa-communication-layer)
    document.addEventListener(INIT_ERROR_EVENT, handleInitErrorEvent);
    document.addEventListener(INIT_ERROR_SEND_REPORT_EVENT, handleSendReportEvent);
    console.log('[INIT UI] ✅ Listening for document events:', INIT_ERROR_EVENT, INIT_ERROR_SEND_REPORT_EVENT);

    // Request current state from tracker (in case we mounted after initialization completed)
    console.log('[INIT UI] 📤 Requesting current state from tracker...');
    document.dispatchEvent(new CustomEvent(INIT_ERROR_REQUEST_EVENT, { bubbles: true }));

    return () => {
      console.log('[INIT UI] Removing document event listeners');
      document.removeEventListener(INIT_ERROR_EVENT, handleInitErrorEvent);
      document.removeEventListener(INIT_ERROR_SEND_REPORT_EVENT, handleSendReportEvent);
    };
  }, []);

  // Send error report when we receive state with errors and initialization is complete
  useEffect(() => {
    if (errors.length > 0 && isComplete && !reportSent) {
      console.log('[INIT UI] 📧 Sending error report (initialization complete with errors)...');
      setReportSent(true);

      // Get user info from localStorage
      const userStr = localStorage.getItem('bt-user');
      let userId: string | undefined;
      let userEmail: string | undefined;

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userId = user._id || user.id;
          userEmail = user.email;
        } catch (e) {
          console.log('[INIT UI] Could not parse user from localStorage');
        }
      }

      const reportData: InitErrorReportData = {
        errors,
        steps,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userId,
        userEmail,
      };

      sendInitErrorReport(reportData)
        .then((result) => {
          if (result.success !== false) {
            console.log('[INIT UI] ✅ Error report sent to backend successfully');
          } else {
            console.log('[INIT UI] ❌ Failed to send error report:', result.error);
          }
        })
        .catch((error) => {
          console.error('[INIT UI] ❌ Error sending report to backend:', error);
        });
    }
  }, [errors, isComplete, reportSent, steps]);

  const handleCopyReport = useCallback(() => {
    if (errors.length === 0) {
      navigator.clipboard.writeText('No initialization errors');
      return;
    }

    let report = `Initialization Errors (${errors.length}):\n\n`;

    errors.forEach((error, index) => {
      report += `${index + 1}. [Step ${error.stepNumber}] ${error.step}\n`;
      report += `   Error: ${error.errorMessage}\n`;
      report += `   Time: ${new Date(error.timestamp).toLocaleTimeString()}\n`;
      if (error.stack) {
        report += `   Stack:\n${error.stack.split('\n').slice(0, 5).map(l => '      ' + l).join('\n')}\n`;
      }
      report += '\n';
    });

    navigator.clipboard.writeText(report).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [errors]);

  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  const toggleStep = useCallback((stepKey: string) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepKey)) {
        newSet.delete(stepKey);
      } else {
        newSet.add(stepKey);
      }
      return newSet;
    });
  }, []);

  // Sort steps by step number
  const sortedSteps = [...steps].sort((a, b) => {
    const aNum = parseFloat(a.stepNumber);
    const bNum = parseFloat(b.stepNumber);
    return aNum - bNum;
  });

  const hasErrors = errors.length > 0;
  const isInitializing = !isComplete && steps.length > 0;

  console.log('[INIT UI] Render decision:', {
    hasErrors,
    isInitializing,
    isComplete,
    stepsLength: steps.length,
    errorsLength: errors.length,
    isDebugEnabled
  });

  // Only show if bt-debug is enabled
  if (!isDebugEnabled) {
    console.log('[INIT UI] 🔇 Returning null - bt-debug not enabled');
    return null;
  }

  // Don't show anything if no errors and initialization is complete or not started
  if (!hasErrors && (isComplete || steps.length === 0)) {
    console.log('[INIT UI] 🔇 Returning null - no errors and (complete or no steps)');
    return null;
  }

  // Show loading indicator during initialization
  if (isInitializing && !hasErrors) {
    console.log('[INIT UI] ⏳ Showing loading indicator');
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={`p-2 hover:bg-gray-100 rounded-full transition-colors ${className || ''}`}
          >
            <Clock size={20} className="text-blue-500 animate-pulse" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Initializing WhatsApp connection...</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  const getStatusIcon = (status: SerializableInitStep['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'failed':
        return <XCircle size={16} className="text-red-500" />;
      case 'running':
        return <Clock size={16} className="text-blue-500 animate-spin" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusColor = (status: SerializableInitStep['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'running':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  console.log('[INIT UI] ⚠️ SHOWING ERROR BUTTON - hasErrors:', hasErrors, 'errors:', errors);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => setIsDialogOpen(true)}
            className={`p-2 hover:bg-red-100 rounded-full transition-colors relative ${className || ''}`}
          >
            <AlertTriangle size={20} className="text-red-500" />
            {hasErrors && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {errors.length}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-semibold text-red-600">Initialization Errors Detected</p>
          <p className="text-sm">Click to see details</p>
        </TooltipContent>
      </Tooltip>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={24} />
              Initialization Errors
            </DialogTitle>
            <DialogDescription>
              The following errors occurred during WhatsApp Web connection initialization.
              This may affect the extension's functionality.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 my-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyReport}
              className="flex items-center gap-1"
            >
              <Copy size={14} />
              {copied ? 'Copied!' : 'Copy Report'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReload}
              className="flex items-center gap-1"
            >
              <RefreshCw size={14} />
              Reload Page
            </Button>
          </div>

          <ScrollArea className="flex-1 pr-4 max-h-[60vh] overflow-auto">
            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-gray-600 uppercase tracking-wide">
                Initialization Steps
              </h4>

              {sortedSteps.map((step) => {
                const stepKey = `${step.stepNumber}-${step.step}`;
                const isExpanded = expandedSteps.has(stepKey);
                const hasError = step.status === 'failed' && step.error;

                return (
                  <div
                    key={stepKey}
                    className={`border rounded-lg p-3 ${getStatusColor(step.status)}`}
                  >
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => hasError && toggleStep(stepKey)}
                    >
                      <div className="flex items-center gap-2">
                        {getStatusIcon(step.status)}
                        <span className="text-xs font-mono text-gray-500">
                          [{step.stepNumber}]
                        </span>
                        <span className="font-medium">{step.step}</span>
                        {step.duration && (
                          <span className="text-xs text-gray-500">
                            ({step.duration}ms)
                          </span>
                        )}
                      </div>
                      {hasError && (
                        isExpanded ? (
                          <ChevronUp size={16} className="text-gray-500" />
                        ) : (
                          <ChevronDown size={16} className="text-gray-500" />
                        )
                      )}
                    </div>

                    {hasError && isExpanded && step.error && (
                      <div className="mt-3 pt-3 border-t border-red-200">
                        <div className="text-sm">
                          <div className="font-semibold text-red-700 mb-1">Error Message:</div>
                          <code className="block bg-red-100 p-2 rounded text-xs text-red-800 whitespace-pre-wrap break-all">
                            {step.error.errorMessage}
                          </code>
                        </div>

                        {step.error.stack && (
                          <div className="text-sm mt-2">
                            <div className="font-semibold text-red-700 mb-1">Stack Trace:</div>
                            <code className="block bg-red-100 p-2 rounded text-xs text-red-800 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                              {step.error.stack}
                            </code>
                          </div>
                        )}

                        {step.error.context && Object.keys(step.error.context).length > 0 && (
                          <div className="text-sm mt-2">
                            <div className="font-semibold text-red-700 mb-1">Context:</div>
                            <code className="block bg-red-100 p-2 rounded text-xs text-red-800 whitespace-pre-wrap">
                              {JSON.stringify(step.error.context, null, 2)}
                            </code>
                          </div>
                        )}

                        <div className="text-xs text-gray-500 mt-2">
                          Time: {new Date(step.error.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {errors.length > 0 && (
                <>
                  <h4 className="font-semibold text-sm text-gray-600 uppercase tracking-wide mt-6">
                    Summary ({errors.length} error{errors.length > 1 ? 's' : ''})
                  </h4>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>What does this mean?</strong> Some WhatsApp Web internal modules
                      failed to load. This can happen due to:
                    </p>
                    <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                      <li>Another browser extension interfering with WhatsApp Web</li>
                      <li>WhatsApp Web updated and some modules changed</li>
                      <li>Network issues during page load</li>
                      <li>Browser cache issues</li>
                    </ul>
                    <p className="text-sm text-yellow-800 mt-2">
                      <strong>Try:</strong> Reloading the page, disabling other extensions,
                      or clearing browser cache.
                    </p>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default memo(InitErrorIndicator);
